import {Draggable, gsap, InertiaPlugin} from "gsap/all";
import $ from "jquery";
import EventEmitter from "eventemitter3";
import tzlookup from "tz-lookup";

export class Weather extends EventEmitter {

  constructor(isMaster = false) {
    super();

    this.isMaster = isMaster; // The Master will send messages to other screens. There's no need for multiple screens to send messages

    this.$widget = $(".weather-bar");
    this.$infoContainer = $(".weather-info-container");
    this.$todayForecastTarget = this.$widget.find('[data-type="today"] .value');
    this.$todayForecastIconTarget = this.$widget.find('.weather-info-icon img');
    this.$locationTarget = this.$widget.find('[data-type="location"] .value');
    this.$clockTarget = this.$widget.find('[data-type="time"] .value');
    this.$temperatureTarget = this.$widget.find('[data-type="temperature_2m"] .value');
    this.$pressureTarget = this.$widget.find('[data-type="surface_pressure"] .value');
    this.$humidityTarget = this.$widget.find('[data-type="relative_humidity_2m"] .value');
    this.$precipitationTarget = this.$widget.find('[data-type="precipitation"] .value');

    this.isFetchingNewData = false;
    this.currentDataTime = null;
    this.currentForecast = null;
    this.weatherData = {};
    this.visitorData = {
      lat: "",
      long: "",
      timezone: "",
    }

    this.apiURL = "";

    this.init();
  }

  async init() {
    this.on("weatherUpdated", (data) => this.onWeatherUpdated());

    this.visitorData = {
      lat: "",
      long: "",
      timezone: "",
      unit: "",
    }

    // Set visitor data to build the url, but add defaults to Montreal to avoid errors in case they are not set in the CMS
    const vc = app.data.cms_content?.visitor_customization ?? {};
    // Coerce to Number — CMS fields can come back as strings, and tzlookup throws on non-numeric input.
    this.visitorData.lat = Number(vc.latitude) || 45.501900;
    this.visitorData.long = Number(vc.longitude) || -73.567400;
    this.visitorData.unit = vc.unit || "celsius";
    this.visitorData.timezone = tzlookup(this.visitorData.lat, this.visitorData.long);

    const defaultCity = app.i18n.lang === "fr" ? "Montréal" : "Montreal";
    this.$locationTarget.html(vc.city || defaultCity);

    this.apiURL = `https://api.open-meteo.com/v1/forecast?latitude=${this.visitorData.lat}&longitude=${this.visitorData.long}&timezone=${this.visitorData.timezone}&daily=weather_code&current=temperature_2m,relative_humidity_2m,wind_speed_10m,precipitation,weather_code,surface_pressure&forecast_days=1&temperature_unit=${this.visitorData.unit}`;

    try {
      await this.loadAPIData();
    } catch (err) {
      console.error("Initial weather load failed:", err);
    }

    this.updateClock();
    setInterval(() => this.updateClock(), 1000);

    this.initDataSync();
  }

  initDataSync() {
    setInterval(async () => {
      if (this.isFetchingNewData) {
        console.log("Fetching new data skipped, already running");
        return; // skip if already fetching data
      }

      this.isFetchingNewData = true;

      try {
        const newDataAvailable = await this.loadAPIData();

        if (newDataAvailable) {

        }
      } catch (err) {
        console.error("Error:", err);
      } finally {
        this.isFetchingNewData = false; // mark as done
      }

    }, app.settings.weather_data_refresh_interval_ms)
  }

  loadAPIData() {
    return new Promise((resolve) => {
      console.log("Loading weather data...");

      $.ajax({
        dataType: "json",
        url: this.apiURL,
        cache: false,
        timeout: 10000,
      }).done((data) => {
        // Validate response shape before trusting it.
        if (!data?.current?.time) {
          console.error("Weather API returned malformed data:", data);
          this.$infoContainer.removeClass("shown");
          resolve(false);
          return;
        }

        // Here we can check if the data has been updated since last sync
        // If the data did not change, do not update the content
        // We'll save the api time so we can compare later
        const isNew = this.currentDataTime !== data.current.time;
        if (isNew) {
          this.currentDataTime = data.current.time;
          this.weatherData = {...data};
          this.emit("weatherUpdated", this.weatherData); // Let other parts of the app know new data is available if necessary
        }

        this.$infoContainer.addClass("shown");
        resolve(isNew);

      }).fail((jqXHR, textStatus) => {
        // Transient failures (timeout, 5xx, offline) shouldn't kill the app —
        // resolve false so the next refresh tick retries naturally.
        console.error(`Weather API request failed: ${textStatus}`, jqXHR?.status);
        this.$infoContainer.removeClass("shown");
        resolve(false);
      });
    });
  }

  onWeatherUpdated() {
    try {
      // new data is available at this.weatherData at this point
      this.currentForecast = this.findWeatherCode(this.weatherData.current?.weather_code);

      // Used to define the weather icon
      this.$todayForecastIconTarget.attr("src", `images/icons/weather-icons/${this.currentForecast.cms_id}.png`);

      this.$todayForecastTarget.text(`${app.data.cms_content.global[this.currentForecast.cms_id]?.[app.i18n.lang] ?? ""}`);

      this.$temperatureTarget.text(`${this.weatherData.current.temperature_2m}${this.weatherData.current_units.temperature_2m}`);
      this.$pressureTarget.text(`${this.weatherData.current.surface_pressure} ${this.weatherData.current_units.surface_pressure}`);
      this.$humidityTarget.text(`${this.weatherData.current.relative_humidity_2m}${this.weatherData.current_units.relative_humidity_2m}`);

      let precipitation = "";
      if (this.weatherData.current.precipitation > 0) {
        precipitation = `${this.weatherData.current.precipitation} ${this.currentForecast.precipitation_unit[app.i18n.lang]}`
      }

      this.$precipitationTarget.text(precipitation);

      if (this.isMaster) {
        // Send current weather to other screens
        app.sendScreenBridgeMessage(`weather ${this.currentForecast.cms_id}`);
      }
    } catch (err) {
      console.error("Failed to render weather update:", err);
    }
  }

  updateClock() {
    const now = new Date();

    const time = now.toLocaleTimeString('en-US', {
      timeZone: this.visitorData.timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const abbr = now.toLocaleTimeString('en-US', {
      timeZone: this.visitorData.timezone,
      timeZoneName: 'short'
    }).split(' ').pop();

    this.$clockTarget.text(`${time} (${abbr})`);
  }

  findWeatherCode(code) {
    const weather = app.data.cms_content?.weather ?? [];
    return weather.find(entry => entry.codes?.includes(code)) ?? weather[0];
  }
}
